import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  HttpError,
  type OrgUser,
  type ProjectId,
  type ProjectMember,
  type ProjectMemberRole,
} from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { projectMembersRepository, projectsRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import {
  AppIcons,
  Button,
  Card,
  IconButton,
  initials,
  Scaffold,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  fireAndForget,
  twcn,
  useOrgContext,
  useSnackbar,
} from '@web/ui/core';

const ROLE_LABEL: Record<ProjectMemberRole, string> = {
  admin: 'project.editor.users.role.admin',
  editor: 'project.editor.users.role.editor',
  viewer: 'project.editor.users.role.viewer',
} as const;

const ROLE_OPTIONS: readonly ProjectMemberRole[] = ['admin', 'editor', 'viewer'] as const;
const ROLE_RANK: Record<ProjectMemberRole, number> = { admin: 1, editor: 2, viewer: 3 };
const DEFAULT_ADD_ROLE: ProjectMemberRole = 'editor';
// Stand-in while the candidates load: one identity, so `filtered` memoizes across renders.
const NO_CANDIDATES: OrgUser[] = [];

export function ProjectUsersPage(): React.JSX.Element | null {
  const { projectId: projectIdParam } = useParams({ strict: false });
  const projectId = projectIdParam as ProjectId;
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const [pickerOpen, setPickerOpen] = useState(false);

  const membersQuery = useQuery({
    queryKey: queryKeys.projects.members(orgId, projectId),
    queryFn: () => projectMembersRepository.list(orgId, projectId),
  });

  const projectQuery = useQuery({
    queryKey: queryKeys.projects.detail(orgId, projectId),
    queryFn: () => projectsRepository.getProject(orgId, projectId),
  });

  const invalidateAll = (): Promise<unknown> =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.members(orgId, projectId) }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.assignableUsers(orgId, projectId),
      }),
    ]);

  const onMutationError = (err: unknown): void => {
    // 403 → caller lost `kanban:project-members:write`. Drop the project
    // detail so the gates re-sync and hide the add/remove/role controls.
    if (err instanceof HttpError && err.status === 403) {
      fireAndForget(
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.detail(orgId, projectId),
        }),
      );
    }
    showSnackbar({
      severity: 'error',
      message: err instanceof Error ? err.message : 'Erro',
    });
  };

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ProjectMemberRole }) =>
      projectMembersRepository.updateRole(orgId, projectId, userId, { role }),
    onSuccess: () => void invalidateAll(),
    onError: onMutationError,
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => projectMembersRepository.remove(orgId, projectId, userId),
    onSuccess: () => void invalidateAll(),
    onError: onMutationError,
  });

  if (membersQuery.isLoading || projectQuery.isLoading) return null;

  const members = membersQuery.data ?? [];
  const ownerUserId = projectQuery.data?.ownerUserId;
  const canWriteMembers = !!projectQuery.data?.permissions.includes('kanban:project-members:write');

  const ordered = [...members].sort((a, b) => {
    const aOwner = a.userId === ownerUserId;
    const bOwner = b.userId === ownerUserId;
    if (aOwner !== bOwner) return aOwner ? -1 : 1;
    const rankDiff = ROLE_RANK[a.role] - ROLE_RANK[b.role];
    if (rankDiff !== 0) return rankDiff;
    return (a.userName || a.userEmail || '').localeCompare(b.userName || b.userEmail || '');
  });

  const ownerCount = ownerUserId && members.some((m) => m.userId === ownerUserId) ? 1 : 0;
  const adminCount = members.filter((m) => m.role === 'admin' && m.userId !== ownerUserId).length;
  const editorCount = members.filter((m) => m.role === 'editor' && m.userId !== ownerUserId).length;
  const viewerCount = members.filter((m) => m.role === 'viewer' && m.userId !== ownerUserId).length;

  const mutating = updateRoleMutation.isPending || removeMutation.isPending;

  return (
    <Scaffold
      body={
        <>
          <div className="mb-3.5">
            <h4 className="m-0 text-base font-medium text-foreground">
              {t('project.editor.users.title')}
            </h4>
            <p className="m-0 text-meta text-muted-foreground">{t('project.editor.users.hint')}</p>
          </div>

          {members.length === 0 ? (
            <Card>
              <div className="py-7 text-center">
                <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <AppIcons name="users" size={20} />
                </div>
                <div className="text-card-meta font-medium text-foreground">
                  {t('project.editor.users.empty.title')}
                </div>
                <div className="mt-0.5 text-meta text-muted-foreground">
                  {t('project.editor.users.empty.hint')}
                </div>
              </div>
            </Card>
          ) : (
            <>
              <MemberStats
                owner={ownerCount}
                admin={adminCount}
                editor={editorCount}
                viewer={viewerCount}
              />
              <ul className="mt-3 flex list-none flex-col gap-1.5 p-0">
                {ordered.map((m) => (
                  <MemberRow
                    key={m.id}
                    member={m}
                    isOwner={m.userId === ownerUserId}
                    disabled={mutating || !canWriteMembers}
                    onRoleChange={(role) => {
                      updateRoleMutation.mutate({ userId: m.userId, role });
                    }}
                    onRemove={() => {
                      if (globalThis.confirm(t('project.editor.users.remove.confirm'))) {
                        removeMutation.mutate(m.userId);
                      }
                    }}
                  />
                ))}
              </ul>
            </>
          )}

          {canWriteMembers ? (
            <div className="mt-3.5">
              {pickerOpen ? (
                <MemberPicker
                  orgId={orgId}
                  projectId={projectId}
                  onClose={() => {
                    setPickerOpen(false);
                  }}
                  onAdded={() => {
                    setPickerOpen(false);
                    fireAndForget(invalidateAll());
                  }}
                />
              ) : (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setPickerOpen(true);
                    }}
                  >
                    <AppIcons name="user-plus" size={13} />
                    <span>{t('project.editor.users.actions.add')}</span>
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </>
      }
    />
  );
}

interface MemberStatsProps {
  owner: number;
  admin: number;
  editor: number;
  viewer: number;
}

function MemberStats({
  owner,
  admin,
  editor,
  viewer,
}: Readonly<MemberStatsProps>): React.JSX.Element {
  return (
    <div className="mb-3 flex items-center gap-3.5 rounded-md border border-border bg-muted px-3 py-2 font-mono text-card-id tracking-eyebrow-narrow text-muted-foreground">
      <span className="inline-flex items-center gap-1.5 font-medium text-vip">
        <AppIcons name="crown" size={12} weight="fill" />
        {owner} {t('project.editor.users.stats.owner')}
      </span>
      <span>
        {admin}{' '}
        {admin === 1
          ? t('project.editor.users.stats.admin')
          : t('project.editor.users.stats.adminPlural')}
      </span>
      <span>
        {editor}{' '}
        {editor === 1
          ? t('project.editor.users.stats.editor')
          : t('project.editor.users.stats.editorPlural')}
      </span>
      <span>
        {viewer}{' '}
        {viewer === 1
          ? t('project.editor.users.stats.viewer')
          : t('project.editor.users.stats.viewerPlural')}
      </span>
    </div>
  );
}

interface MemberRowProps {
  member: ProjectMember;
  isOwner: boolean;
  disabled: boolean;
  onRoleChange: (role: ProjectMemberRole) => void;
  onRemove: () => void;
}

function MemberRow({
  member,
  isOwner,
  disabled,
  onRoleChange,
  onRemove,
}: Readonly<MemberRowProps>): React.JSX.Element {
  const displayName = member.userName || member.userEmail || member.userId;
  const ownerHint = t('project.editor.users.owner.hint');
  return (
    <li>
      <div
        className={twcn(
          'grid w-full grid-cols-member-row items-center gap-3 rounded-lg border py-2 pl-2.5 pr-2.5 transition-colors',
          isOwner
            ? 'border-vip/30 bg-gradient-to-b from-vip-tint to-card'
            : 'border-border bg-card hover:border-border-strong/40',
        )}
      >
        <span
          aria-hidden
          className={twcn(
            'inline-flex h-8 w-8 items-center justify-center rounded-pill border font-mono text-card-id font-medium tracking-eyebrow-narrow',
            isOwner ? 'border-vip/40 bg-card text-vip' : 'border-border bg-muted text-foreground',
          )}
        >
          {initials(displayName)}
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-card-meta font-medium text-foreground">{displayName}</span>
          {member.userEmail && member.userName ? (
            <span className="truncate text-micro text-muted-foreground">{member.userEmail}</span>
          ) : null}
        </span>
        {isOwner ? (
          <span
            title={ownerHint}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-vip/30 bg-vip-tint px-2.5 text-meta font-medium text-vip"
          >
            <AppIcons name="crown" size={12} weight="fill" />
            <span className="flex-1">{t('project.editor.users.owner.badge')}</span>
            <AppIcons name="lock" size={11} />
          </span>
        ) : (
          <Select
            disabled={disabled}
            value={member.role}
            onValueChange={(v) => {
              onRoleChange(v as ProjectMemberRole);
            }}
          >
            <SelectTrigger
              aria-label={t('project.editor.users.role.label')}
              className="h-7 w-role-select px-2 text-meta"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {t(ROLE_LABEL[r] as Parameters<typeof t>[0])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <IconButton
          variant={isOwner ? 'ghost' : 'danger'}
          size="sm"
          icon={isOwner ? 'lock' : 'trash'}
          disabled={disabled || isOwner}
          onClick={onRemove}
          title={isOwner ? ownerHint : undefined}
          aria-label={t('project.editor.users.actions.remove')}
        />
      </div>
    </li>
  );
}

interface MemberPickerProps {
  orgId: string;
  projectId: ProjectId;
  onClose: () => void;
  onAdded: () => void;
}

function MemberPicker({
  orgId,
  projectId,
  onClose,
  onAdded,
}: Readonly<MemberPickerProps>): React.JSX.Element {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const candidatesQuery = useQuery({
    queryKey: queryKeys.projects.assignableUsers(orgId, projectId),
    queryFn: () => projectMembersRepository.listAssignableUsers(orgId, projectId),
  });

  const addMutation = useMutation({
    mutationFn: (userId: string) =>
      projectMembersRepository.add(orgId, projectId, { userId, role: DEFAULT_ADD_ROLE }),
    onSuccess: () => {
      onAdded();
    },
    onError: (err) => {
      // Same 403 re-sync as role/remove: drop the project detail so the
      // gates hide the picker on next render if the caller was demoted.
      if (err instanceof HttpError && err.status === 403) {
        fireAndForget(
          queryClient.invalidateQueries({
            queryKey: queryKeys.projects.detail(orgId, projectId),
          }),
        );
      }
      showSnackbar({
        severity: 'error',
        message: err instanceof Error ? err.message : 'Erro',
      });
    },
  });

  // External DOM listeners — no React-native primitive for click-outside.
  useEffect(() => {
    inputRef.current?.focus();
    const onMouseDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [onClose]);

  const candidates = candidatesQuery.data ?? NO_CANDIDATES;
  const filtered = useMemo<OrgUser[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [candidates, query]);

  let candidatesContent: React.ReactNode;
  if (candidatesQuery.isLoading) {
    candidatesContent = null;
  } else if (filtered.length === 0) {
    candidatesContent = (
      <div className="px-3 py-4 text-center text-meta text-muted-foreground">
        {candidates.length === 0
          ? t('project.editor.users.picker.allAssigned')
          : t('project.editor.users.picker.empty')}
      </div>
    );
  } else {
    candidatesContent = filtered.map((u) => {
      const displayName = u.name || u.email || u.userId;
      return (
        <button
          key={u.userId}
          type="button"
          disabled={addMutation.isPending}
          onClick={() => {
            addMutation.mutate(u.userId);
          }}
          className="group grid grid-cols-member-picker items-center gap-3 border-b border-border bg-transparent px-2.5 py-2 text-left transition-colors last:border-b-0 hover:bg-primary-tint disabled:opacity-50"
        >
          <span
            aria-hidden
            className="inline-flex h-8 w-8 items-center justify-center rounded-pill border border-border bg-muted font-mono text-card-id font-medium tracking-eyebrow-narrow text-foreground"
          >
            {initials(displayName)}
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-card-meta font-medium text-foreground group-hover:text-primary-ink">
              {displayName}
            </span>
            {u.email && u.name ? (
              <span className="truncate text-micro text-muted-foreground">{u.email}</span>
            ) : null}
          </span>
          <span className="inline-flex h-6 items-center gap-1 rounded-md border border-border px-2 text-card-id font-medium text-muted-foreground transition-colors group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground">
            <AppIcons name="plus" size={11} />
            <span>{t('project.editor.users.actions.addToProject')}</span>
          </span>
        </button>
      );
    });
  }

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label={t('project.editor.users.actions.add')}
      className="overflow-hidden rounded-lg border border-border bg-card shadow-md"
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <AppIcons name="magnifying-glass" size={14} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={t('project.editor.users.picker.placeholder')}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              onClose();
            }
          }}
          className="flex-1 border-0 bg-transparent text-base text-foreground outline-none placeholder:text-faint-foreground"
        />
        <IconButton
          variant="ghost"
          size="xs"
          icon="close"
          onClick={onClose}
          aria-label={t('project.editor.users.picker.cancel')}
        />
      </div>
      <div className="flex max-h-72 flex-col overflow-y-auto">{candidatesContent}</div>
    </div>
  );
}
